import React, { useState, useCallback, useMemo, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useSelector } from '@xstate/react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCurrentUserId } from 'selectors/accountSelectors'
import CommentInput from './CommentInput'
import CommentList from './CommentList'
import type { DeleteCommentModalResult } from './DeleteCommentModal'
import DeleteCommentModal from './DeleteCommentModal'
import { useMultiCartComments } from 'hooks/SharedCart/useMultiCartComments'
import { useCreateMultiCartComment } from 'hooks/SharedCart/useCreateMultiCartComment'
import { useUpdateMultiCartComment } from 'hooks/SharedCart/useUpdateMultiCartComment'
import { useDeleteMultiCartComment } from 'hooks/SharedCart/useDeleteMultiCartComment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { cartCommentNewEvent, cartCommentEditEvent, cartCommentDeleteEvent } from 'analytics/snowplow/events'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: 0 0 ${props => props.theme.borderRadius.S} ${props => props.theme.borderRadius.S};
  padding: ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  
  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
  
  &.no-border {
    border-left: none;
    border-right: none;
    border-bottom: none;
  }
`

interface Props {
  multiCartId: string
  itemIds: Array<string>
  isAdmin?: boolean
  noBorder?: boolean
}

function SharedCartComments({
  multiCartId,
  itemIds,
  isAdmin = false,
  noBorder = false,
}: Props) {
  const intl = useIntl()
  const currentUserId = useAppSelector(getCurrentUserId)
  const showModal = useContext(ModalContext)
  const multiCartActorRef = MultiCartContext.useActorRef()
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const cartName = cartSummary.status === 'loaded' ? cartSummary.data[multiCartId]?.name : undefined
  const [editingCommentId, setEditingCommentId] = useState<string | undefined>()
  const [inputValue, setInputValue] = useState('')

  const { data: comments = [], isLoading } = useMultiCartComments({
    multiCartId,
    itemIds,
    enabled: !!multiCartId && itemIds.length > 0,
    polling: true,
  })

  const createMutation = useCreateMultiCartComment()
  const updateMutation = useUpdateMultiCartComment()
  const deleteMutation = useDeleteMultiCartComment()

  const filteredComments = useMemo(() => {
    return (comments ?? []).filter((comment: App.MultiCartComment) => itemIds.includes(comment.itemId))
  }, [comments, itemIds])

  const primaryItemId = itemIds[0]

  const handleCreateComment = useCallback((text: string) => {
    if (!primaryItemId) return

    createMutation.mutate({
      multiCartId,
      itemId: primaryItemId,
      text,
    })
    if (cartName) cartCommentNewEvent(cartName)
    setInputValue('')
  }, [multiCartId, primaryItemId, createMutation, cartName])

  const handleEditComment = useCallback((comment: App.MultiCartComment) => {
    setEditingCommentId(comment.id)
  }, [])

  const handleUpdateComment = useCallback((commentId: string, text: string) => {
    updateMutation.mutate({
      multiCartId,
      commentId,
      text,
    })
    if (cartName) cartCommentEditEvent(cartName)
    setEditingCommentId(undefined)
  }, [multiCartId, updateMutation, cartName])

  const handleCancelEdit = useCallback((_commentId: string) => {
    setEditingCommentId(undefined)
  }, [])

  const openDeleteModal = useCallback(async(comment: App.MultiCartComment) => {
    const result = await showModal<DeleteCommentModalResult>(<DeleteCommentModal />)

    if (result === 'delete') {
      deleteMutation.mutate({
        multiCartId,
        commentId: comment.id,
      })
      if (cartName) cartCommentDeleteEvent(cartName)
    }
  }, [showModal, deleteMutation, multiCartId, cartName])

  const handleCancelInput = useCallback(() => {
    setInputValue('')
  }, [])

  return (
    <Container className={clsx({ 'no-border': noBorder })}>
      <CommentInput
        onSubmit={handleCreateComment}
        onCancel={handleCancelInput}
        placeholder={intl.formatMessage({
          id: 'G8Ir82hdFu',
          defaultMessage: 'Add a comment...',
          description: 'Placeholder text for comment input in shared cart',
        })}
        initialValue={inputValue}
        disabled={createMutation.isLoading || isLoading}
      />
      {filteredComments.length > 0 && (
        <CommentList
          comments={filteredComments}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          editingCommentId={editingCommentId}
          onEdit={handleEditComment}
          onDelete={openDeleteModal}
          onUpdateComment={handleUpdateComment}
          onCancelEdit={handleCancelEdit}
          onEmptyEdit={openDeleteModal}
          showLeadingDivider
        />
      )}
    </Container>
  )
}

export default SharedCartComments
